import { ActionIcon, Button, Title } from "@mantine/core";
import { IconEdit, IconPlus } from "@tabler/icons-react";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Outlet } from "@tanstack/react-router";
import parsePhoneNumber from "libphonenumber-js";
import { MantineReactTable, MRT_ColumnDef, useMantineReactTable } from "mantine-react-table";
import { httpPostGraphql } from "shared/api/httpClient";
import { Content } from "shared/components/global/Content";
import { usePermissionCheck } from "shared/stores/oidc";
import { graphql } from "src/gql";
import { Permission } from "src/gql/graphql";
import z from "zod";

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const senderTableSchema = z.object({
    id: z.string(),
    name: z.string(),
    fromEmail: z.string().optional(),
    fromPhone: z.string().optional(),
    group: z.object({
        id: z.string(),
        name: z.string(),
    }),
    isSystem: z.boolean(),
});

type SenderTableSchema = z.infer<typeof senderTableSchema>;

const EditButton = ({ groupId, senderId }: { groupId: string; senderId: string }) => {
    const hasEditPermission = usePermissionCheck(Permission.GroupSenderManage, groupId);
    if (!hasEditPermission) return null;

    const nav = Route.useNavigate();

    return (
        <ActionIcon
            variant="subtle"
            onClick={() => void nav({ to: "/organization/senders/$senderId", params: { senderId } })}
            aria-label="Edit sender"
        >
            <IconEdit size={16} />
        </ActionIcon>
    );
};

const columns: MRT_ColumnDef<SenderTableSchema>[] = [
    {
        accessorKey: "name",
        header: "Name",
    },
    {
        accessorKey: "group.name",
        header: "Group",
    },
    {
        accessorKey: "fromPhone",
        header: "Phone Number",
        Cell: ({ cell }) => {
            const maybePhone = cell.getValue<SenderTableSchema["fromPhone"]>();
            const parsedPhone = parsePhoneNumber(maybePhone ?? "", "US");
            if (parsedPhone) {
                return parsedPhone.formatNational();
            }
            return maybePhone;
        },
    },
    {
        accessorKey: "fromEmail",
        header: "Email",
    },
    {
        id: "edit",
        header: "Edit",
        columnDefType: "display",
        size: 50,
        Cell: ({ row }) => {
            return <EditButton senderId={row.id} groupId={row.original.group.id} />;
        },
    },
];

const Page = () => {
    const nav = Route.useNavigate();
    const { data, isFetching } = useSuspenseQuery(senderPageQuery);

    const table = useMantineReactTable({
        columns,
        data: data.senders.toSorted((a, b) => a.group.name.localeCompare(b.group.name)),
        enableColumnActions: false,
        enableBottomToolbar: false,
        enablePagination: false,
        enableColumnFilters: false,
        enableHiding: false,
        enableDensityToggle: false,
        enableFullScreenToggle: false,
        enableSorting: false,
        getRowId: (row) => row.id,
        positionGlobalFilter: "left",
        initialState: { showGlobalFilter: true },
        state: { isLoading: isFetching },
        renderToolbarInternalActions: () => (
            <Button variant="default" leftSection={<IconPlus size={16} />} onClick={() => void nav({ to: "/organization/senders/create" })}>
                Create Sender
            </Button>
        ),
        mantineTopToolbarProps: {
            style: {
                borderBottom: "calc(0.0625rem * var(--mantine-scale)) solid var(--paper-border-color)",
            },
        },
        mantineSearchTextInputProps: {
            "aria-label": "Search senders",
        },
    });

    return (
        <Content paper style={{ border: "none" }}>
            <Content.Heading>
                <Title order={3} size="h1">
                    Senders
                </Title>
            </Content.Heading>
            <Outlet />
            <MantineReactTable table={table} />
        </Content>
    );
};

/** @public */
export const Route = createFileRoute("/_auth/organization/senders")({
    component: Page,
});

export const senderPageQuery = queryOptions({
    queryKey: ["senders", "table"],
    queryFn: () =>
        httpPostGraphql(
            graphql(`
                query SendersTable {
                    senders {
                        id
                        name
                        fromEmail
                        fromPhone
                        group {
                            id
                            name
                        }
                        isSystem
                        verificationSender
                        blockers {
                            isBlocked
                            messages {
                                subject
                                id
                            }
                            templates {
                                id
                                message {
                                    subject
                                    id
                                }
                            }
                        }
                    }
                }
            `),
            {},
        ),
});
